'use client';
import useSearchParams from '@/hooks/useSearchParams';
import { useTranslations } from 'next-intl';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import ArrowIcon from '@/components/ui/icons/arrow-icon';
import { useSortOptions } from '@/store/sort.store';
import { sortKeys } from '@/constants';
import { event_filters_used } from '@/utils/gtm';

function SortAndResult({
  totalProductsCount,
  // categoryName,
  isSearchPage,
  onSort,
  currentPage,
  pageSize,
}) {
  const t = useTranslations('common');

  const { getFilterObj, setFilterQueryHandler } = useSearchParams();
  const [show, setShow] = useState(false);

  const { sortOptions, sortIndex } = useSortOptions();

  const options = isSearchPage
    ? sortOptions
    : sortKeys.map((item) => ({
        value: item.code,
        label: t(item.text),
      }));

  const sortMenuRef = useRef();

  useOnClickOutside([sortMenuRef], () => show && setShow(false));

  const activeSort = useMemo(() => {
    const currentSortValue = new URLSearchParams({
      sortBy: getFilterObj.sortBy || '',
      sortDir: getFilterObj.sortDir || '',
    }).toString();

    return (
      options.find((option) => option.value === currentSortValue) ||
      options[0] ||
      null
    );
  }, [getFilterObj, options]);

  const handleChange = useCallback(
    (selectedOption) => {
      event_filters_used({
        filter_name: 'Sort By',
        filter_value: selectedOption?.label,
        filter_value_id: selectedOption?.value,
        filter_action: 'apply',
      });

      if (isSearchPage) {
        onSort?.(selectedOption?.value);
        setShow(false);

        return;
      }
      setFilterQueryHandler(
        Object.fromEntries(new URLSearchParams(selectedOption?.value || ''))
      );
      setShow(false);
    },
    [setFilterQueryHandler]
  );

  const selected = options.find((opt) => opt.value === sortIndex) || options[0];

  const from = pageSize * currentPage + 1;
  const to =
    pageSize * (currentPage + 1) > totalProductsCount
      ? totalProductsCount
      : pageSize * (currentPage + 1);
  return (
    <div
      className={`${!isSearchPage && 'mb-[13px] flex max-h-[54px] items-center justify-between rounded-[10px] border border-[#00000026] px-[27px] py-[21px] max-sm:hidden'}`}
    >
      {!isSearchPage && (
        <p className='text-lg'>
          {t('Results')} :<strong>{from}</strong>-<strong>{to}</strong>{' '}
          {t('Of')} <strong>{totalProductsCount}</strong> {t('Products')}
        </p>
      )}

      <div className='flex items-center gap-7'>
        <span className='text-lg'>{t('sort_by_key')}</span>
        <div ref={sortMenuRef} className='relative'>
          <div
            onClick={() => setShow((prev) => !prev)}
            className='border-primary flex h-10 min-w-34.5 cursor-pointer items-center justify-center gap-3 overflow-hidden rounded-lg border bg-amber-100 p-1.75'
          >
            <span className='text-center text-sm'>
              {isSearchPage
                ? selected?.label
                : activeSort?.label || t('select_key')}
            </span>
            <div>
              <ArrowIcon className='mt-0.5 size-3.5' />
            </div>
          </div>

          {show && (
            <div
              className='absolute end-0 z-[999] mt-2 min-w-36 rounded-md border border-gray-100 bg-white shadow-lg'
              role='menu'
            >
              <div className='p-2'>
                {options?.map((item) => (
                  <button
                    onClick={() => handleChange(item)}
                    key={item?.value}
                    className={`hover:text-red block w-full rounded-lg px-4 py-2 text-start text-sm hover:bg-gray-50 ${
                      item?.value ===
                      (isSearchPage ? selected?.value : activeSort?.value)
                        ? 'text-primary font-semibold'
                        : ''
                    }`}
                    role='menuitem'
                  >
                    {item?.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SortAndResult;
